'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { SUPPORTED_PIN_LOGOS } from './consts';
import { Location, MapLocatorMapProps } from './types';

// Default to SF if no geolocation is available
const initLat = 37.7749;
const initLng = -122.4194;
const minZoom = 10;
const defZoom = 13;
const idleDebounceMs = 300;

export const MapLocatorMap = ({ onLocationSelect, userCoords, setUserCoords }: MapLocatorMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const cacheRef = useRef<Record<string, Location[]>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showResults = useCallback(
    (results: Location[]) => {
      if (!map) return;

      // Clear existing markers before adding new ones
      for (const marker of markersRef.current) {
        marker.setMap(null);
      }
      markersRef.current = [];

      if (!results.length) {
        return;
      }

      results.forEach(result => {
        const { name, coordinates, address, merchant_logo, opening_hours } = result;

        const latLng = new google.maps.LatLng(coordinates.latitude, coordinates.longitude);

        const pinLogo = merchant_logo && SUPPORTED_PIN_LOGOS.has(merchant_logo) ? merchant_logo : 'atm';

        const marker = new google.maps.Marker({
          position: latLng,
          map: map,
          icon: {
            url: `/map/pins/${pinLogo}.webp`,
            scaledSize: new google.maps.Size(35, 42),
          },
        });

        marker.addListener('click', () => {
          onLocationSelect({
            name,
            address,
            coordinates,
            opening_hours,
            merchant_logo,
          });
        });

        markersRef.current.push(marker);
      });
    },
    [map, onLocationSelect],
  );

  const updateMap = useCallback(
    (swLat: number, swLng: number, neLat: number, neLng: number) => {
      const locKey = `${swLat}:${swLng}:${neLat}:${neLng}`;
      if (cacheRef.current[locKey]) {
        showResults(cacheRef.current[locKey]);
        return;
      }

      fetch(
        `https://locations.chime.com/locations?sw_lat=${swLat}&sw_lng=${swLng}&ne_lat=${neLat}&ne_lng=${neLng}&limit=100&location_types[]=ATM`,
      )
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then(data => {
          cacheRef.current[locKey] = data.result;
          showResults(data.result);
        });
    },
    [showResults],
  );

  const initMap = (lat: number, lng: number) => {
    if (!mapRef.current) return;
    const center = new google.maps.LatLng(lat, lng);

    // Define map options, disable default UI and POI labels
    const options: google.maps.MapOptions = {
      center,
      zoom: defZoom,
      minZoom,
      styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      disableDefaultUI: true,
    };

    const newMap = new google.maps.Map(mapRef.current, options);
    setMap(newMap);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const init = async () => {
      let retries = 5;
      while (!window.google?.maps?.Map && retries > 0) {
        await new Promise(res => setTimeout(res, 200));
        retries--;
      }
      if (!window.google?.maps?.Map) {
        console.warn('Google Maps not available');
        return;
      }

      // Show map immediately with default location
      initMap(initLat, initLng);

      // Request geolocation in parallel — pan when it resolves
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          pos => {
            setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => {
            // Geolocation denied/unavailable — map already showing default location
          },
          { timeout: 10000 },
        );
      }
    };

    init();
  }, [setUserCoords]);

  useEffect(() => {
    if (!map) return;

    const getMapBounds = (map: google.maps.Map) => {
      const bounds = map.getBounds();
      if (!bounds) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      updateMap(sw.lat(), sw.lng(), ne.lat(), ne.lng());
    };

    // Debounce map bounds updates to avoid rapid API calls during pan/zoom
    const boundsListener = google.maps.event.addListener(map, 'idle', () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const center = map.getCenter();
        if (center) {
          getMapBounds(map);
        }
      }, idleDebounceMs);
    });

    // Handle user coordinates updates
    if (userCoords) {
      const newCenter = new google.maps.LatLng(userCoords.lat, userCoords.lng);
      map.setCenter(newCenter);
    }

    return () => {
      clearTimeout(debounceRef.current);
      google.maps.event.removeListener(boundsListener);
    };
  }, [map, userCoords, updateMap]);

  return (
    <div
      ref={mapRef}
      id="gmap"
      data-testid="gmap"
      className="sm:rounded-[10px] w-full h-full aspect-square sm:aspect-auto"
    />
  );
};
